<template>
  <v-card :loading="loading" :disabled="loading" class="student-timetable">
    <div class="hero-panel">
      <v-card-title class="d-flex flex-wrap align-start hero-header">
        <div class="hero-copy">
          <div class="text-h5 font-weight-bold hero-title">
            {{ $t("student_timetable.title") }}
          </div>
          <div class="text-body-2 hero-subtitle mt-2">
            {{ $t("student_timetable.subtitle") }}
          </div>
        </div>

        <v-spacer></v-spacer>
      </v-card-title>
    </div>

    <v-card-text class="content-shell">
      <v-sheet class="filter-panel pa-4 mb-4">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="selectedTerm"
              :items="termOptions"
              item-text="label"
              item-value="value"
              filled
              rounded
              dense
              hide-details
              background-color="white"
              color="deep-orange lighten-1"
              prepend-inner-icon="mdi-calendar-month-outline"
              class="soft-field"
              :label="$t('student_timetable.year_semester')"
              @change="onTermChange"
            ></v-select>
          </v-col>
          <v-col v-if="canEditStudentId" cols="12" sm="6" md="3">
            <v-text-field
              v-model="studentIdInput"
              filled
              rounded
              dense
              hide-details
              background-color="white"
              color="deep-orange lighten-1"
              prepend-inner-icon="mdi-card-account-details-outline"
              class="soft-field"
              :label="$t('student_timetable.student_id')"
              @keyup.enter="fetchData"
            ></v-text-field>
          </v-col>
          <v-col
            v-if="canEditStudentId"
            cols="12"
            sm="6"
            md="2"
            class="d-flex justify-center justify-sm-start justify-md-center"
          >
            <v-btn
              color="deep-orange lighten-1"
              class="white--text search-btn"
              @click="fetchData"
            >
              <v-icon left>mdi-magnify</v-icon>
              {{ $t("teach_table.search") }}
            </v-btn>
          </v-col>
          <v-col v-if="subjects.length > 0" cols="12" md="4" class="d-flex justify-end updated-at-col">
            <v-chip small class="meta-chip" color="orange lighten-5" text-color="brown darken-1">
              <v-icon left small>mdi-update</v-icon>
              {{ $t("student_timetable.updated_at") }}: {{ formattedUpdatedAt }}
            </v-chip>
          </v-col>
        </v-row>

        <v-row dense class="mt-1">
          <v-col cols="12" class="chip-row d-flex flex-wrap">
            <v-chip
              small
              class="meta-chip mr-2 mb-2"
              color="amber lighten-5"
              text-color="deep-orange darken-2"
            >
              <v-icon left small>mdi-calendar-range</v-icon>
              {{ $t("student_timetable.year_semester") }}: {{ selectedTermLabel }}
            </v-chip>
            <v-chip
              v-if="studentId"
              small
              class="meta-chip mr-2 mb-2"
              color="light-blue lighten-5"
              text-color="blue-grey darken-2"
            >
              <v-icon left small>mdi-account-badge-outline</v-icon>
              {{ $t("student_timetable.student_id") }}: {{ studentId }}
            </v-chip>
            <div v-if="subjects.length > 0" class="download-actions">
              <v-btn
                small
                outlined
                color="deep-orange"
                class="download-action mr-2 mb-2"
                elevation="3"
                :loading="downloadingType === 'jpg'"
                :disabled="!!downloadingType"
                @click="downloadTimetable('jpg')"
              >
                <v-icon left small>mdi-file-image-outline</v-icon>
                JPG
              </v-btn>
              <v-btn
                small
                color="deep-orange"
                class="download-action white--text mb-2"
                elevation="4"
                :loading="downloadingType === 'pdf'"
                :disabled="!!downloadingType"
                @click="downloadTimetable('pdf')"
              >
                <v-icon left small>mdi-file-pdf-box</v-icon>
                PDF
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-sheet>

      <v-alert v-if="error" type="error" dense outlined class="mb-4 feedback-alert feedback-alert--error">
        {{ $t("teach_table.error") }}
      </v-alert>

      <v-alert
        v-if="!loading && subjects.length === 0 && studentId && !error"
        type="info"
        dense
        outlined
        class="feedback-alert feedback-alert--info"
      >
        {{ $t("student_timetable.no_registered_subject") }}
      </v-alert>

      <div v-if="subjects.length > 0">
        <v-sheet ref="scheduleExport" class="schedule-panel">
          <div class="schedule-wrap">
            <div class="schedule-grid">
              <div class="corner-cell"></div>
              <div v-for="slot in hourSlots" :key="slot.start" class="time-cell">
                {{ slot.label }}
              </div>

              <div v-for="day in days" :key="day.value" class="day-fragment">
                <div class="day-cell">
                  {{ day.label }}
                </div>
                <div class="day-row">
                  <div
                    v-for="n in totalQuarterSlots - 1"
                    :key="`${day.value}-line-${n}`"
                    class="quarter-line"
                    :style="{ left: `calc(${(n / totalQuarterSlots) * 100}% - 1px)` }"
                  ></div>
                  <div
                    v-for="event in eventsByDay[day.value]"
                    :key="event.key"
                    class="class-block"
                    :class="[event.colorClass, { 'class-block--clickable': event.hasTimeSlot }]"
                    :style="event.style"
                    v-tooltip="event.tooltip"
                    :role="event.hasTimeSlot ? 'button' : null"
                    :tabindex="event.hasTimeSlot ? 0 : null"
                    @click="showSubjectTimeSlot(event.subject)"
                    @keyup.enter="showSubjectTimeSlot(event.subject)"
                  >
                    <div class="class-code">{{ event.subject_id }}</div>
                    <div class="class-name">{{ event.subjectName }}</div>
                    <div class="class-meta">
                      {{ $t("teach_table.section") }} {{ event.section }}
                    </div>
                    <div class="class-room" :class="{ 'class-room--empty': !event.room }">
                      <template v-if="event.room">{{ event.room }}</template>
                      <template v-else>&nbsp;</template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-sheet>

        <v-sheet ref="tableExport" class="table-panel mt-4">
          <v-simple-table dense class="subject-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center">{{ $t("teach_table.course_id") }}</th>
                  <th>{{ $t("teach_table.course_name") }}</th>
                  <th class="text-center">{{ $t("teach_table.section") }}</th>
                  <th>{{ $t("teach_table.teaching_date") }}</th>
                  <th>{{ $t("teach_table.room") }}</th>
                  <th>{{ $t("teach_table.lecturer") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="subject in subjects" :key="subject.rowKey">
                  <td class="text-center">{{ subject.subject_id }}</td>
                  <td>
                    <a class="subject-link" @click="openSubjectDialog(subject.subject_id)">
                      {{ subject.subjectName }}
                    </a>
                  </td>
                  <td class="text-center">{{ subject.section }}<div><lect-or-prac-chip :item="subject" /></div></td>
                  <td>
                    <div v-for="(teachDay, index) in subject.teach_days" :key="index" class="teach-time">
                      <div>
                        <span>{{ formatTeachDay(teachDay) }}</span>
                      </div>
                    </div>
                  </td>
                  <td>{{ subject.roomText || "-" }}</td>
                  <td>
                    <div v-if="resolveTeacherLines(subject).length > 0">
                      <div
                        v-for="(teacher, teacherIndex) in resolveTeacherLines(subject)"
                        :key="`${subject.rowKey}-teacher-${teacherIndex}`"
                        class="teacher-line"
                      >
                        {{ teacher }}
                      </div>
                    </div>
                    <span v-else>-</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-sheet>
      </div>

      <timetable-day-dialog v-model="timeSlotDialog" :subject="showingSubject" :level-id="levelId"
        :teachers="showingSubject ? resolveTeacherLines(showingSubject) : []"
        @details="openSubjectDialog($event.subject_id)" />
      <subject-dialog ref="subject_dialog" :level-id="levelId" />
    </v-card-text>
  </v-card>
</template>

<script>
import * as dayjs from "dayjs";
require("dayjs/locale/th");
import RegistrationProvider from "@/services/registration.provider.js";
import UserCentralProvider from "@/services/user.central.provider.js";
import { parseDate_Time } from "@/helper/registration";
import { dateTimeFromDBTranslatorFull } from "@/helper/translator";
import LectOrPracChip from '@/views/teach_table/components/LectOrPracChip.vue';
import TimetableDayDialog from './TimetableDayDialog.vue';
import SubjectDialog from "@/views/teach_table/components/SubjectDialog.vue";
import {
  createPrintPage,
  renderExportElementToCanvas,
  saveJpgPages,
  savePdfPages,
} from "./timetableExport";

const RegistrationService = new RegistrationProvider();
const UserCentralService = new UserCentralProvider();

export default {
  name: "StudentTimetable",
  components: {
    SubjectDialog,
    LectOrPracChip,
    TimetableDayDialog,
  },
  data: () => ({
    loading: false,
    error: false,
    year: null,
    semester: null,
    currentYear: null,
    currentSemester: null,
    studentIdInput: null,
    selectedStudentInfo: null,
    selectedStudentInfoStudentId: null,
    subjects: [],
    updatedAt: null,
    showingSubject: null,
    timeSlotDialog: false,
    downloadingType: null,
    startHour: 8,
    endHour: 21,
    colors: [
      "block-sunday",
      "block-monday",
      "block-tuesday",
      "block-wednesday",
      "block-thursday",
      "block-friday",
      "block-saturday",
    ],
  }),
  computed: {
    selectedTerm: {
      get() {
        if (!this.year || !this.semester) return null;
        return `${this.year}-${this.semester}`;
      },
      set(value) {
        if (!value) {
          this.year = null;
          this.semester = null;
          return;
        }

        const [year, semester] = value.split("-");
        this.year = year;
        this.semester = semester;
      },
    },
    selectedTermLabel() {
      if (!this.year || !this.semester) return "-";
      return `${this.semester} / ${this.formatAcademicYear(this.year)}`;
    },
    formattedUpdatedAt() {
      return this.updatedAt ? dateTimeFromDBTranslatorFull(this.updatedAt) : "-";
    },
    studentId() {
      if (this.studentIdInput) return String(this.studentIdInput).trim();
      return this.$store.state.user.surrogated_student_id;
    },
    currentStudentInfo() {
      if (this.canEditStudentId && this.studentIdInput) {
        return this.selectedStudentInfoStudentId === this.studentId
          ? this.selectedStudentInfo
          : null;
      }
      return this.$store.state.user.surrogated_student_info;
    },
    studentName() {
      const studentInfo = this.currentStudentInfo;
      if (!studentInfo) return "-";
      const name =
        this.$i18n.locale === "en"
          ? [studentInfo.fname_en, studentInfo.lname_en]
          : [studentInfo.fname_th, studentInfo.lname_th];
      return name.filter(Boolean).join(" ") || "-";
    },
    exportMetadata() {
      return {
        title: this.$t("student_timetable.title"),
        studentIdLabel: this.$t("student_timetable.student_id"),
        studentId: this.studentId,
        studentNameLabel: this.$t("pre_regis_panel.name_surname"),
        studentName: this.studentName,
        termLabel: this.$t("student_timetable.year_semester"),
        term: this.selectedTermLabel,
        updatedAtLabel: this.$t("student_timetable.updated_at"),
        updatedAt: this.formattedUpdatedAt,
      };
    },
    canEditStudentId() {
      return this.$store.state.user.is_authorized_officer 
    },
    levelId() {
      const studentInfo = this.currentStudentInfo;
      const privilege =
        studentInfo &&
        studentInfo.payload &&
        studentInfo.payload.ticket &&
        studentInfo.payload.ticket.privilege;

      if (!privilege) return "1";
      return privilege == 80 ? "1" : "2";
    },
    days() {
      return [
        { value: "1", label: this.$t("student_timetable.sunday") },
        { value: "2", label: this.$t("student_timetable.monday") },
        { value: "3", label: this.$t("student_timetable.tuesday") },
        { value: "4", label: this.$t("student_timetable.wednesday") },
        { value: "5", label: this.$t("student_timetable.thursday") },
        { value: "6", label: this.$t("student_timetable.friday") },
        { value: "7", label: this.$t("student_timetable.saturday") },
      ];
    },
    hourSlots() {
      const slots = [];
      for (let hour = this.startHour; hour < this.endHour; hour += 1) {
        slots.push({
          start: hour,
          label: `${this.pad(hour)}:00 - ${this.pad(hour + 1)}:00`,
        });
      }
      return slots;
    },
    totalQuarterSlots() {
      return (this.endHour - this.startHour) * 4;
    },
    termOptions() {
      const baseYear = Number(this.currentYear || this.year || dayjs().year() + 543);
      const options = [];
      for (let y = baseYear; y >= baseYear - 5; y -= 1) {
        ["3", "2", "1"].forEach((semester) => {
          options.push({
            label: `${semester} / ${this.formatAcademicYear(y)}`,
            value: `${y}-${semester}`,
          });
        });
      }
      return options;
    },
    eventsByDay() {
      const grouped = this.days.reduce((acc, day) => {
        acc[day.value] = [];
        return acc;
      }, {});

      return this.events.reduce((acc, event) => {
        if (!acc[event.day]) acc[event.day] = [];
        acc[event.day].push(event);
        return acc;
      }, grouped);
    },
    events() {
      const startMinute = this.startHour * 60;
      const endMinute = this.endHour * 60;
      const usableMinutes = endMinute - startMinute;

      return this.subjects.flatMap((subject) => {
        return subject.teach_days
          .map((teachDay, teachIndex) => {
            const eventStart = this.toMinutes(teachDay.time);
            const eventEnd = this.toMinutes(teachDay.time2);

            if (
              eventStart === null ||
              eventEnd === null ||
              eventEnd <= startMinute ||
              eventStart >= endMinute ||
              eventEnd <= eventStart
            ) {
              return null;
            }

            const clampedStart = Math.max(eventStart, startMinute);
            const clampedEnd = Math.min(eventEnd, endMinute);
            const left = ((clampedStart - startMinute) / usableMinutes) * 100;
            const width = ((clampedEnd - clampedStart) / usableMinutes) * 100;
            const room = subject.roomText;

            return {
              key: `${subject.rowKey}-${teachIndex}`,
              day: teachDay.day,
              subject_id: subject.subject_id,
              subjectName: subject.subjectName,
              section: subject.section,
              room,
              subject,
              hasTimeSlot: this.hasTimeSlot(subject),
              colorClass: this.dayColorClass(teachDay.day),
              tooltip: [subject.subjectName, this.formatTeachDay(teachDay), room].filter(Boolean).join(" | "),
              style: {
                left: `calc(${left}% - 1px)`,
                width: `calc(${width}% + 1px)`,
              },
            };
          })
          .filter(Boolean);
      });
    },
  },
  methods: {
    dayjs,
    pad(value) {
      return String(value).padStart(2, "0");
    },
    formatAcademicYear(year) {
      const numericYear = Number(year);
      if (this.$i18n.locale === "en" && numericYear > 2400) return numericYear - 543;
      return year;
    },
    dayColorClass(dayValue) {
      const dayIndex = Number(dayValue) - 1;
      if (Number.isNaN(dayIndex)) return this.colors[0];
      return this.colors[((dayIndex % this.colors.length) + this.colors.length) % this.colors.length];
    },
    toMinutes(time) {
      if (!time) return null;
      const [hour, minute] = time.split(":");
      if (hour === undefined || minute === undefined) return null;
      return Number(hour) * 60 + Number(minute);
    },
    stripHtml(value) {
      if (!value) return "";
      return String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    },
    resolveTeacherHtml(subject) {
      if (!subject) return "";
      if (this.$i18n.locale === "en") {
        return subject.teacher_list_en || subject.teacher_list_th || subject.teacher_list || "";
      }
      return subject.teacher_list_th || subject.teacher_list_en || subject.teacher_list || "";
    },
    resolveTeacherLines(subject) {
      return this.extractHtmlLines(this.resolveTeacherHtml(subject));
    },
    extractHtmlLines(value) {
      if (!value) return [];
      return String(value)
        .split(/<\/div>\s*<div>|<div>|<\/div>|<br\s*\/?>/i)
        .map((item) => this.stripHtml(item))
        .filter(Boolean);
    },
    formatTeachDay(teachDay) {
      const day = this.days.find((item) => item.value === teachDay.day);
      return `${day ? day.label : ""} ${teachDay.time.substring(0, 5)} - ${teachDay.time2.substring(0, 5)}`;
    },
    hasTimeSlot(subject) {
      return subject && subject.time_slot && subject.time_slot.length > 0;
    },
    showSubjectTimeSlot(subject) {
      if (!this.hasTimeSlot(subject)) return;
      this.showingSubject = subject;
      this.timeSlotDialog = true;
    },
    openSubjectDialog(subjectId) {
      if (this.$refs.subject_dialog) {
        this.$refs.subject_dialog.show_item_detail(subjectId);
      }
    },
    async downloadTimetable(type) {
      if (!this.$refs.scheduleExport || !this.$refs.tableExport) return;

      try {
        this.downloadingType = type;
        await this.$nextTick();

        const pages = await this.buildExportPages();
        const baseName = this.exportFileBaseName();

        if (type === "pdf") savePdfPages(pages, baseName);
        else await saveJpgPages(pages, baseName);
      } catch (error) {
        if (this.$toasted) {
          this.$toasted.error(error.message || "Download failed", { duration: 3000 });
        }
      } finally {
        this.downloadingType = null;
      }
    },
    async buildExportPages() {
      const scheduleCanvas = await renderExportElementToCanvas(this.$refs.scheduleExport, this.exportMetadata, {
        orientation: "landscape",
      });
      const tableCanvas = await renderExportElementToCanvas(this.$refs.tableExport, this.exportMetadata, {
        orientation: "landscape",
        hideSelector: ".teach-time .v-btn",
      });

      return [
        {
          canvas: createPrintPage(scheduleCanvas, "landscape"),
          orientation: "landscape",
          fileSuffix: "page-1-schedule",
        },
        {
          canvas: createPrintPage(tableCanvas, "landscape"),
          orientation: "landscape",
          fileSuffix: "page-2-table",
        },
      ];
    },
    exportFileBaseName() {
      return [
        "student-timetable",
        this.studentId || "student",
        this.year || "year",
        this.semester || "semester",
      ].join("-");
    },
    normalizeSubject(item, index) {
      const parsed = parseDate_Time(item);
      const subjectName =
        parsed.subject_ename ||
        parsed.subject_tname ||
        parsed.subject_name_en ||
        parsed.subject_name_th ||
        "";
      const teacherHtml =
        this.$i18n.locale === "en"
          ? parsed.teacher_list_en || parsed.teacher_list || ""
          : parsed.teacher_list_th || parsed.teacher_list || "";
      const roomText = [parsed.classroom, parsed.classbuilding].filter(Boolean).join(" ");

      return {
        ...parsed,
        rowKey: `${parsed.teach_table_id || parsed.subject_id}-${parsed.section}-${index}`,
        subjectName,
        roomText,
        teacher_list: teacherHtml,
      };
    },
    async getCurrentTerm() {
      const data = await RegistrationService.getYearSemesterNow(this.levelId);
      this.currentYear = data.YEAR;
      this.currentSemester = data.SEMESTER;
      if (!this.year) this.year = data.YEAR;
      if (!this.semester) this.semester = data.SEMESTER;
    },
    async ensureSelectedStudentInfo() {
      if (!this.canEditStudentId || !this.studentIdInput) return;

      const studentId = String(this.studentIdInput).trim();
      if (
        this.selectedStudentInfo &&
        this.selectedStudentInfoStudentId === studentId
      ) {
        return;
      }

      const studentInfo = await UserCentralService.getAuthUserInfo(studentId);
      if (studentId !== String(this.studentIdInput || "").trim()) return;

      this.selectedStudentInfo = studentInfo;
      this.selectedStudentInfoStudentId = studentId;
    },
    syncQuery() {
      this.$router
        .replace({
          name: "student_timetable",
          query: {
            ...this.$route.query,
            student_id: this.studentId,
            year: this.year,
            semester: this.semester,
            level_id: this.levelId,
          },
        })
        .catch(() => {});
    },
    onTermChange() {
      this.fetchData();
    },
    async fetchData() {
      if (!this.studentId || !this.year || !this.semester) return;

      try {
        this.loading = true;
        this.error = false;
        await this.ensureSelectedStudentInfo();
        const data = await RegistrationService.getRegisResult({
          student_id: this.studentId,
          year: this.year,
          semester: this.semester,
          level_id: this.levelId,
        });
        this.subjects = data.map((item, index) => this.normalizeSubject(item, index));
        this.updatedAt = dayjs().format("YYYY-MM-DD HH:mm:ss");
        this.syncQuery();
      } catch (error) {
        this.error = true;
        this.subjects = [];
        if (error.response && error.response.data && error.response.data.message) {
          this.$toasted.error(JSON.stringify(error.response.data.message), {
            duration: 0,
          });
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchInit() {
      this.studentIdInput = this.$route.query.student_id || null;
      this.year = this.$route.query.year || null;
      this.semester = this.$route.query.semester || null;
      await this.ensureSelectedStudentInfo().catch(() => {});
      await this.getCurrentTerm();
      await this.fetchData();
    },
  },
  created() {
    this.fetchInit();
  },
};
</script>

<style scoped>
.student-timetable {
  max-width: 1440px;
  margin: 0 auto;
  background: #fffdfb;
}

.hero-panel {
  background: #fff3ea;
  border-bottom: 1px solid rgba(224, 182, 146, 0.42);
}

.hero-header {
  gap: 16px;
  padding: 28px 32px 24px;
}

.hero-copy {
  max-width: 680px;
}

.hero-title {
  color: #5d4036;
  font-family: "Anuphan", sans-serif !important;
  letter-spacing: 0;
}

.hero-subtitle {
  color: #87685d;
  max-width: 54ch;
}

.content-shell {
  padding: 24px;
}

.filter-panel,
.schedule-panel,
.table-panel {
  overflow: hidden;
  border: 1px solid #f1e1d6;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 16px 30px rgba(171, 126, 97, 0.08);
}

.filter-panel {
  background: #fff8f4;
}

.soft-field {
  border-radius: 10px;
}

.search-btn {
  width: 100%;
  max-width: 168px;
  height: 42px !important;
  letter-spacing: 0.01em;
}

.meta-chip {
  font-weight: 500;
}

.updated-at-col {
  margin-left: auto;
}

.download-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-left: auto;
}

.download-action {
  box-shadow: 0 6px 14px rgba(216, 91, 43, 0.18) !important;
}

.teacher-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.teach-time {
  display: flex;
  width: max-content;
  align-items: center;
  white-space: nowrap;
}

.feedback-alert {
  border-radius: 10px;
}

.feedback-alert--error {
  background: #fff4f2 !important;
}

.feedback-alert--info {
  background: #f8fbff !important;
}

.schedule-wrap {
  overflow-x: hidden;
  overflow-y: hidden;
  background: #fffdfb;
  -webkit-overflow-scrolling: touch;
}

.schedule-grid {
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: 90px repeat(13, minmax(0, 1fr));
}

.schedule-grid,
.schedule-grid * {
  box-sizing: border-box;
}

.day-fragment {
  display: contents;
}

.corner-cell,
.time-cell,
.day-cell {
  background: #fdf0e5;
  border-right: 1px solid #ead2c0;
  border-bottom: 1px solid #ead2c0;
  color: #614439;
  font-family: "Sarabun", sans-serif;
  font-weight: 700;
}

.corner-cell,
.time-cell {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-cell {
  padding: 0 4px;
  font-size: 12px;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
}

.day-cell {
  min-height: 86px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff7f1;
  font-size: 14px;
  padding: 0 4px;
  text-align: center;
}

.day-row {
  grid-column: span 13;
  position: relative;
  min-height: 86px;
  overflow: visible;
  border-bottom: 1px solid #f0e6df;
  background: #fffdfb;
}

.quarter-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(149, 120, 100, 0.04);
  pointer-events: none;
}

.quarter-line:nth-child(4n) {
  background: rgba(149, 120, 100, 0.12);
}

.block-sunday {
  background: #f8dede;
}

.block-monday {
  background: #f7efc8;
}

.block-tuesday {
  background: #f5dff0;
}

.block-wednesday {
  background: #dff1de;
}

.block-thursday {
  background: #f9e3cc;
}

.block-friday {
  background: #dce9f7;
}

.block-saturday {
  background: #e9def8;
}

.subject-table {
  background: transparent;
}

.subject-table thead {
  background: #fdf0e1;
}

.subject-table th {
  color: #5d4036 !important;
  font-family: "Sarabun", sans-serif !important;
  font-size: 12px !important;
  letter-spacing: 0;
  text-transform: uppercase;
  padding: 4px 10px !important;
  border-bottom: none !important;
}

.subject-table td {
  border-color: #f3e6dc !important;
  padding: 4px 10px !important;
}

.subject-table tbody tr:nth-child(even) {
  background: #fffaf6;
}

.subject-table tbody tr:hover {
  background: #fff3ea;
}

@media (max-width: 960px) {
  .hero-header {
    padding: 24px 20px 20px;
  }

  .content-shell {
    padding: 20px;
  }

  .schedule-wrap {
    overflow-x: auto;
  }

  .schedule-grid {
    width: max(100%, 1137px);
    min-width: 0;
    grid-template-columns: 90px repeat(13, minmax(79px, 1fr));
  }
}

@media (max-width: 600px) {
  .student-timetable,
  .filter-panel,
  .schedule-panel,
  .table-panel {
    border-radius: 10px;
  }

  .content-shell {
    padding: 16px;
  }

  .search-btn {
    max-width: none;
  }

  .download-actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }
}

</style>

<style scoped src="./timetableBlocks.css"></style>
<style scoped src="./timetableInteraction.css"></style>
